'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { DropdownMenu, DropdownMenuEntry } from '@hrworks/sui-core/DropdownMenu';

import { Button } from '../Button';
import { SOCIAL_ICON_MAP } from '../SocialIcons/SocialIcon/socialIconMap';
import type { SharePopoverProps } from './SharePopover.types';

const ShareIcon = ({ iconKey }: { iconKey: keyof typeof SOCIAL_ICON_MAP }) => {
  const config = SOCIAL_ICON_MAP[iconKey];
  if (!config) return null;
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" css={{ flexShrink: 0, marginRight: 5 }}>
      <path d={config.path} fill="currentColor" />
    </svg>
  );
};

const shareLinks = (url: string, title: string) => [
  {
    key: 'linkedin' as const,
    label: 'LinkedIn',
    href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  },
  {
    key: 'twitter' as const,
    label: 'X (Twitter)',
    href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    key: 'xing' as const,
    label: 'Xing',
    href: `https://www.xing.com/spi/shares/new?url=${encodeURIComponent(url)}`,
  },
  {
    key: 'facebook' as const,
    label: 'Facebook',
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  { key: 'whatsapp' as const, label: 'WhatsApp', href: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}` },
  {
    key: 'email' as const,
    label: 'E-Mail',
    href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`,
  },
];

/** Ensures the URL is absolute by prepending window.location.origin if needed */
const ensureAbsoluteUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (typeof window === 'undefined') return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const SharePopover = ({ url, title }: SharePopoverProps) => {
  const t = useTranslations('Share');
  const [copied, setCopied] = useState(false);

  const absoluteUrl = ensureAbsoluteUrl(url);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(absoluteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTriggerClick = async (e: React.MouseEvent) => {
    if ('ontouchstart' in window && navigator.share) {
      e.preventDefault();
      e.stopPropagation();
      try {
        await navigator.share({ title, url: absoluteUrl });
      } catch {
        // User cancelled
      }
    }
  };

  return (
    <DropdownMenu
      placement="bottom-end"
      trigger={
        <Button data-guide-id="jobdetail-share-btn" variant="subtle" onClick={handleTriggerClick}>
          {t('share')}
        </Button>
      }
      items={
        <>
          <DropdownMenuEntry onClick={handleCopyLink}>
            <ShareIcon iconKey="link" />
            {copied ? t('copied') : t('copy-link')}
          </DropdownMenuEntry>
          {shareLinks(absoluteUrl, title).map(({ key, label, href }) => (
            <DropdownMenuEntry key={key} href={href} target="_blank">
              <ShareIcon iconKey={key} />
              {label}
            </DropdownMenuEntry>
          ))}
        </>
      }
    />
  );
};
